/* eslint-disable react-hooks/exhaustive-deps */

import React, { ReactElement, useCallback, useEffect, useState } from 'react';

import { Container, Grid, GridItem, Spinner } from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { useLocation, useNavigate } from '@interstellar/react-app-routing';
import SmsNeedHelp from 'components/smsNeedHelp/SmsNeedHelp';
import PageHeading from 'components/pageHeading/PageHeading';
import StyledBox from 'components/style/styledBox/StyledBox';

import { StyledGrid } from './SmsOtp.styled';
import { SmsOtpContent } from './SmsOtpContent';
import { otpVerificationInit } from '../../api';
import { generateUuid } from '../../utils/generateUuid';
import { getRedirectURI } from '../../utils/configUtils';

function SmsOtp(): ReactElement {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { smsOtpContent } = useContent<SmsOtpContent>();
  const [
    {
      serviceName,
      loaderMessage,
      yesBtnLink,
      sessionTimedOut,
      smsOtpAuthorize,
      technicalErrorPage,
      unableToVerify,
    },
  ] = smsOtpContent;
  const navigate = useNavigate();
  const { state } = useLocation();

  // Callback triggers when user enters otp and clicks submit
  const onSuccessCallbackHandler = useCallback(
    (successData: Record<string, string>): void => {
      if (
        successData.code === undefined ||
        successData.code === '' ||
        successData.code === 'TECHNICAL_ERROR' ||
        successData.code === 'NO_AUTH_METHOD'
      )
        navigate(technicalErrorPage);
      else if (
        successData.code === 'SESSION_EXPIRED' ||
        successData.code === 'AUTHENTICATION_TIMEOUT'
      ) {
        navigate(sessionTimedOut);
      } else if (successData.code === 'AUTHENTICATION_FAILURE') {
        navigate(unableToVerify);
      } else navigate(`${smsOtpAuthorize}?authCode=${successData.code}`);
    },
    [],
  );

  // Callback triggers when user cancels the journey
  const onFailHandler = useCallback(
    (errorData: Record<string, string>): void => {
      console.log('errorData: ', errorData);
      if (
        errorData.authStatus === undefined ||
        errorData.authStatus === '' ||
        errorData.authStatus === 'TECHNICAL_ERROR' ||
        errorData.authStatus === 'NO_AUTH_METHOD'
      )
        navigate(technicalErrorPage);
      else if (
        errorData.authStatus === 'SESSION_EXPIRED' ||
        errorData.authStatus === 'AUTHENTICATION_TIMEOUT'
      ) {
        navigate(sessionTimedOut);
      } else if (errorData.authStatus === 'AUTHENTICATION_FAILURE') {
        navigate(unableToVerify);
      } else {
        navigate(yesBtnLink);
      }
    },
    [],
  );

  const renderPageContent = useCallback(
    (response: any): any => {
      const script = document.createElement('script');
      script.id = 'sca-auth-script';
      script.src = response.AuthCwaJsUrl;

      script.async = true;
      script.onSuccess = onSuccessCallbackHandler;
      script.onFail = onFailHandler;

      script.setAttribute('data-goto', response.GotoUrl);
      script.setAttribute(
        'data-auth-context-token',
        response.AuthenticationContextToken,
      );
      script.setAttribute('data-session-id', generateUuid());
      script.setAttribute('data-channel-name', 'BB');
      script.setAttribute('data-source-channel-name', 'O4B');
      const container = document.getElementById('main');
      container.appendChild(script);
      setIsLoading(false);
    },
    [onSuccessCallbackHandler, onFailHandler],
  );

  const callOtpVerificationInit = useCallback(async (): Promise<void> => {
    const body = {
      Channel: 'BB',
      EventType: 'ADD_REMOVE_PARTY_UNAUTH',
      SourceChannel: 'O4B',
      SubChannel: 'DESKTOP',
      State: 'registration',
      Scope: ['openid o4b_pm_unauth'],
      RedirectUri: getRedirectURI(),
      ServiceRequestId: state?.userData?.serviceRequestId,
      ItemId: generateUuid(),
      ItemType: 'O4B_REGISTRATION',
    };

    try {
      const response = await otpVerificationInit(body);
      localStorage.setItem('CodeVerifier', response.data.CodeVerifier);
      localStorage.setItem('State', response.data.State);
      renderPageContent(response.data);
    } catch (err) {
      console.log('err: ', err);
      navigate(technicalErrorPage);
    }
  }, [renderPageContent]);

  useEffect(() => {
    callOtpVerificationInit();
  }, [callOtpVerificationInit]);

  return (
    <Container padding="none">
      <PageHeading serviceName={serviceName} />
      <Grid alignX="center" alignY="top">
        <GridItem lg={8}>
          <StyledBox>
            <StyledGrid alignX="center" alignY="center">
              <GridItem>
                <div id="main" data-testid="sca-auth-cwa-main">
                  <div id="sca-auth-cwa">
                    {isLoading && <Spinner message={loaderMessage} />}
                  </div>
                </div>
              </GridItem>
            </StyledGrid>
          </StyledBox>
        </GridItem>

        <GridItem lg={4}>
          <SmsNeedHelp />
        </GridItem>
      </Grid>
    </Container>
  );
}

export default SmsOtp;
